import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { NativeSelect } from "../ui/native-select";
import { Alert, AlertDescription } from "../ui/alert";
import PageHeader from "../PageHeader";
import Creature from "./Creature";
import usePointer from "./usePointer";
import { triviaApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { DICTIONARY_URL, adminQuestionsUrl, playAddress } from "./game";

const SECONDS = [10, 15, 20, 30, 45];

/**
 * /trivia: start a game (projects/trivia). The game itself runs at
 * /trivia/:id, which is what goes on the projector.
 */
function TriviaHost() {
  const navigate = useNavigate();
  const look = usePointer();
  const [seconds, setSeconds] = useState("20");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // A create that lands after the host went elsewhere mustn't pull them back.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const { data } = await triviaApi.create(Number(seconds));
      if (mounted.current) navigate(`/trivia/${data.id}`);
    } catch (err) {
      if (!mounted.current) return;
      setError(apiErrorMessage(err, "Couldn't start a game."));
      setBusy(false);
    }
  }

  return (
    <div className="page-container space-y-6 py-6">
      <PageHeader title="Trivia" />
      <div className="flex flex-col gap-6 rounded-lg border bg-card p-6 md:flex-row md:items-center">
        <div className="flex shrink-0 gap-2" aria-hidden="true">
          {[0, 1, 2].map((kind) => (
            <Creature key={kind} kind={kind} size={72} look={look} />
          ))}
        </div>
        <div className="space-y-2">
          <p>
            Start a game and put it on the big screen. Players open{" "}
            <strong>{playAddress()}</strong> on their phones and type the code it shows. They
            don&apos;t need a mono account.
          </p>
          <p className="text-sm text-muted-foreground">
            The game asks every question that&apos;s turned on, lowest order first.{" "}
            <a className="underline" href={adminQuestionsUrl()} target="_blank" rel="noreferrer">
              Edit or turn questions on and off in Django admin
            </a>
            .
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="trivia-seconds">Time per question</Label>
          <NativeSelect
            id="trivia-seconds"
            value={seconds}
            onChange={(e) => setSeconds(e.target.value)}
            disabled={busy}
          >
            {SECONDS.map((s) => (
              <option key={s} value={String(s)}>
                {s} seconds
              </option>
            ))}
          </NativeSelect>
        </div>
        <Button className="h-11 md:h-10" onClick={create} disabled={busy}>
          {busy ? "Starting…" : "New game"}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <p className="text-sm text-muted-foreground">
        Questions written from Matt Pocock&apos;s{" "}
        <a className="underline" href={DICTIONARY_URL} target="_blank" rel="noreferrer">
          AI Coding Dictionary
        </a>
        .
      </p>
    </div>
  );
}

export default TriviaHost;
